import React, { useCallback, useEffect, useMemo } from 'react';
import { noop } from 'lodash';
import MuiRadio from '@mui/material/Radio';
import MuiRadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import Checkbox from '../checkbox';
export interface IRadioProps {
    label: React.ReactNode;
    value: string | number | boolean;
    children?: React.ReactNode;
}
interface IRadioGroupProps {
    items: IRadioProps[];
    name: string;
    dataTestId?: string;
    optionType?: 'radio' | 'checkbox';
    radioGroupClassName?: string;
    buttonClassName?: string;
    onSelectChange?: (value: string | number | boolean) => void;
    onOptionClick?: (item: IRadioProps, e: React.MouseEvent<HTMLDivElement>) => void;
    value?: string | number | boolean | null;
    controlled?: boolean;
    required?: boolean;
    fullViewClickable?: boolean;
    formControl?: boolean;
    noIcon?: boolean;
}

export const sizes = ['sm', 'sm-max', 'sm-min', 'md', 'md-min', 'md-max', 'lg-min', 'lg', 'xl', '2xl', 'xl-min', '2xl-min'];

export const RadioGroup = React.forwardRef<HTMLDivElement, IRadioGroupProps>(
    (
        {
            required = false,
            controlled = false,
            onOptionClick,
            onSelectChange,
            items,
            radioGroupClassName = '',
            buttonClassName = '',
            dataTestId = '',
            fullViewClickable = false,
            formControl = true,
            noIcon = false,
            ...rest
        },
        ref
    ) => {
        const [localSelected, setSelectedValue] = React.useState<string | number | boolean | null>(null);

        const handleChange = useCallback(
            (event: React.ChangeEvent<HTMLInputElement>) => {
                const val = event.target.value;
                setSelectedValue(val);
                if (controlled) {
                    return;
                }
                onSelectChange && onSelectChange(val);
            },
            [controlled, onSelectChange]
        );

        const handleClick = useCallback(
            (e: React.MouseEvent<HTMLButtonElement | HTMLDivElement, MouseEvent>, item: IRadioProps) => {
                if (!controlled) {
                    return;
                }
                e.stopPropagation();
                e.preventDefault();
                onOptionClick && onOptionClick(item, e);
            },
            [controlled, onOptionClick]
        );

        useEffect(() => {
            if (undefined === rest.value || null === rest.value) {
                return setSelectedValue(null);
            }
            if (null === localSelected || rest.value.toString() !== localSelected.toString()) {
                setSelectedValue(rest.value);
            }
        }, [rest.value, localSelected]);

        const options = useMemo(
            () =>
                items.map((item, idx) => {
                    const isChecked = null !== localSelected && localSelected.toString() === item.value.toString();
                    const sizes = ['sm', 'sm-max', 'sm-min', 'md', 'md-min', 'md-max', 'lg-min', 'lg', 'xl', '2xl', 'xl-min', '2xl-min'];
                    const sizedOptionClassNames = sizes
                        .map(size => `${buttonClassName}-${size} ${size}:${buttonClassName}-${size}__${idx}`)
                        .join(' ');
                    const sizedRadioBtnClassNames = sizes
                        .map(
                            size =>
                                `${buttonClassName}-option ${buttonClassName}-option__${idx} ${size}:${buttonClassName}-option-${size}__${idx}`
                        )
                        .join(' ');
                    const RadioOption = () =>
                        rest.optionType === 'radio' ? (
                            <MuiRadio
                                data-testid={`radio-option-${dataTestId}-${item.value}`}
                                value={item.value}
                                checked={isChecked}
                                onChange={handleChange}
                                onClick={e => handleClick(e, item)}
                                className={`${buttonClassName}-option ${buttonClassName}-radio ${buttonClassName}-option__${idx} ${sizedRadioBtnClassNames} ${isChecked ? 'checked' : ''}`}
                            />
                        ) : (
                            <Checkbox
                                value={item.value}
                                data-testid={`radio-option-${dataTestId}-${item.value}`}
                                checked={isChecked}
                                onChange={handleChange}
                                onClick={e => handleClick(e, item)}
                                className={`${buttonClassName}-option ${buttonClassName}-checkbox ${buttonClassName}-option__${idx} ${sizedRadioBtnClassNames} ${isChecked ? 'checked' : ''}`}
                            />
                        );
                    return (
                        <div
                            className={`flex items-center sm-max:items-start ${buttonClassName} ${isChecked ? 'checked' : ''} ${buttonClassName}-${idx} ${sizedOptionClassNames}`}
                            key={idx}
                            onClick={fullViewClickable ? e => handleClick(e, item) : noop}
                        >
                            {!noIcon && <RadioOption />}
                            <span className={`${buttonClassName}-label ${isChecked ? 'checked' : ''}`}>{item.label}</span>
                            {item.children}
                        </div>
                    );
                }),
            [buttonClassName, handleChange, handleClick, items, localSelected, rest.optionType]
        );

        const content = (
            <MuiRadioGroup {...rest} data-testid={dataTestId} value={localSelected} className={radioGroupClassName}>
                {options}
            </MuiRadioGroup>
        );

        return formControl
            ? <FormControl ref={ref} className="radio-group-form-control">{content}</FormControl>
            : <div ref={ref} className="radio-group-form-control">{content}</div>;
    }
);

export default RadioGroup;
